import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import './AdminEmployeesSection.css';

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const EMPTY_FILTER = { company_id: '', employee_id: '', month: '', year: new Date().getFullYear().toString() };

function filterAndSort(list, filterText, sortKey, sortDir, textFields) {
  let arr = [...(list || [])];
  if (filterText.trim()) {
    const q = filterText.toLowerCase().trim();
    arr = arr.filter(item =>
      textFields.some(f => (item[f] || '').toLowerCase().includes(q))
    );
  }
  if (sortKey && sortDir) {
    arr.sort((a, b) => {
      const va = a[sortKey] ?? '';
      const vb = b[sortKey] ?? '';
      const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }
  return arr;
}

function normId(item) {
  if (!item) return undefined;
  const id = item.id ?? item._id;
  return typeof id === 'object' && id?.$oid != null ? id.$oid : id;
}

function AdminEmployeesSection() {
  const [subTab, setSubTab] = useState('companies');
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTER);
  const [filterText, setFilterText] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, dir: 'asc' });

  const [formCompany, setFormCompany] = useState({ company_name: '', address: '', contact_person: '', contact_number: '', email: '', status: 'active' });
  const [formEmployee, setFormEmployee] = useState({ full_name: '', email: '', phone: '', designation: '', company_id: '', joining_date: '', login_username: '', password: '', status: 'active' });
  const [formAttendance, setFormAttendance] = useState({ employee_id: '', date: '', punch_in_time: '', punch_out_time: '' });
  const toast = useToast();

  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await api.get('/companies');
      setCompanies((data || []).map(c => ({ ...c, id: normId(c) })));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadEmployees = useCallback(async () => {
    try {
      const { data } = await api.get('/employees');
      setEmployees((data || []).map(em => {
        const cid = em.company_id;
        const companyIdStr = cid == null ? '' : (typeof cid === 'object' && cid?.$oid ? cid.$oid : String(cid));
        return { ...em, id: normId(em), company_id: companyIdStr };
      }));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadAttendances = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.company_id) params.set('company_id', filters.company_id);
      if (filters.employee_id) params.set('employee_id', filters.employee_id);
      if (filters.month) params.set('month', filters.month);
      if (filters.year) params.set('year', filters.year);
      const { data } = await api.get(`/attendances?${params}`);
      setAttendances((data || []).map(a => ({ ...a, id: normId(a) })));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadCompanies();
    loadEmployees();
  }, [loadCompanies, loadEmployees]);

  useEffect(() => {
    if (subTab === 'attendance') loadAttendances();
  }, [subTab, loadAttendances]);

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    try {
      const companyId = editing && (normId(editing) || editing.id || editing._id);
      if (companyId) {
        await api.put(`/companies/${companyId}`, { company: formCompany });
      } else {
        await api.post('/companies', { company: formCompany });
      }
      setShowForm(false);
      setEditing(null);
      setFormCompany({ company_name: '', address: '', contact_person: '', contact_number: '', email: '', status: 'active' });
      loadCompanies();
      loadEmployees();
      toast.success(editing ? 'Company updated successfully' : 'Company added successfully');
    } catch (err) {
      toast.error(err.response?.data?.errors?.join('\n') || 'Failed to save company');
    }
  };

  const handleSaveEmployee = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formEmployee };
      if (formEmployee.password) payload.password = formEmployee.password;
      else delete payload.password;
      const employeeId = editing && (normId(editing) || editing.id || editing._id);
      if (employeeId) {
        await api.put(`/employees/${employeeId}`, { employee: payload });
      } else {
        await api.post('/employees', { employee: payload });
      }
      setShowForm(false);
      setEditing(null);
      setFormEmployee({ full_name: '', email: '', phone: '', designation: '', company_id: '', joining_date: '', login_username: '', password: '', status: 'active' });
      loadEmployees();
      toast.success(editing ? 'Employee updated successfully' : 'Employee added successfully');
    } catch (err) {
      toast.error(err.response?.data?.errors?.join('\n') || 'Failed to save employee');
    }
  };

  const handleDeactivateEmployee = async (emp) => {
    if (!window.confirm(`Deactivate ${emp.full_name}?`)) return;
    try {
      await api.delete(`/employees/${normId(emp) || emp.id}`);
      loadEmployees();
      toast.success('Employee deactivated');
    } catch (e) {
      toast.error('Failed to deactivate');
    }
  };

  const handleSaveAttendance = async (e) => {
    e.preventDefault();
    try {
      const attendanceId = editing && (normId(editing) || editing.id || editing._id);
      if (attendanceId) {
        await api.put(`/attendances/${attendanceId}`, {
          attendance: {
            punch_in_time: formAttendance.punch_in_time || undefined,
            punch_out_time: formAttendance.punch_out_time || undefined
          }
        });
      } else {
        await api.post('/attendances', {
          attendance: {
            employee_id: formAttendance.employee_id,
            date: formAttendance.date,
            punch_in_time: formAttendance.punch_in_time || undefined,
            punch_out_time: formAttendance.punch_out_time || undefined
          }
        });
      }
      setShowForm(false);
      setEditing(null);
      setFormAttendance({ employee_id: '', date: '', punch_in_time: '', punch_out_time: '' });
      loadAttendances();
      toast.success(editing ? 'Attendance updated successfully' : 'Attendance added successfully');
    } catch (err) {
      toast.error(err.response?.data?.errors?.join('\n') || 'Failed to save attendance');
    }
  };

  const handleDownloadReport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.company_id) params.set('company_id', filters.company_id);
      if (filters.employee_id) params.set('employee_id', filters.employee_id);
      if (filters.month) params.set('month', filters.month);
      if (filters.year) params.set('year', filters.year);
      const res = await api.get(`/attendances/report?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_report_${filters.month || 'all'}_${filters.year || 'all'}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error('Failed to download report');
    }
  };

  const openEditAttendance = (att) => {
    setEditing(att);
    setFormAttendance({
      employee_id: att.employee_id,
      date: att.date,
      punch_in_time: att.punch_in_time || '',
      punch_out_time: att.punch_out_time || ''
    });
    setShowForm(true);
  };

  return (
    <div className="admin-employees-section">
      <div className="employees-sub-tabs">
        <button className={subTab === 'companies' ? 'active' : ''} onClick={() => { setSubTab('companies'); setShowForm(false); setEditing(null); }}>Companies</button>
        <button className={subTab === 'employees' ? 'active' : ''} onClick={() => { setSubTab('employees'); setShowForm(false); setEditing(null); }}>Employees</button>
        <button className={subTab === 'attendance' ? 'active' : ''} onClick={() => { setSubTab('attendance'); setShowForm(false); setEditing(null); }}>Attendance</button>
      </div>

      {subTab === 'companies' && (
        <>
          <div className="list-header">
            <h2>Companies</h2>
            <button className="btn-add" onClick={() => { setShowForm(true); setEditing(null); setFormCompany({ company_name: '', address: '', contact_person: '', contact_number: '', email: '', status: 'active' }); }}>+ Add Company</button>
          </div>
          {showForm ? (
            <form className="employees-form" onSubmit={handleSaveCompany}>
              <h3>{editing ? 'Edit' : 'Add'} Company</h3>
              <div className="form-field">
                <label htmlFor="comp-company_name">Company name</label>
                <input id="comp-company_name" value={formCompany.company_name} onChange={e => setFormCompany(c => ({ ...c, company_name: e.target.value }))} required />
              </div>
              <div className="form-field">
                <label htmlFor="comp-address">Address</label>
                <input id="comp-address" value={formCompany.address} onChange={e => setFormCompany(c => ({ ...c, address: e.target.value }))} />
              </div>
              <div className="form-field">
                <label htmlFor="comp-contact_person">Contact person</label>
                <input id="comp-contact_person" value={formCompany.contact_person} onChange={e => setFormCompany(c => ({ ...c, contact_person: e.target.value }))} />
              </div>
              <div className="form-field">
                <label htmlFor="comp-contact_number">Contact number</label>
                <input id="comp-contact_number" value={formCompany.contact_number} onChange={e => setFormCompany(c => ({ ...c, contact_number: e.target.value }))} />
              </div>
              <div className="form-field">
                <label htmlFor="comp-email">Email</label>
                <input id="comp-email" type="email" value={formCompany.email} onChange={e => setFormCompany(c => ({ ...c, email: e.target.value }))} />
              </div>
              <div className="form-field">
                <label htmlFor="comp-status">Status</label>
                <select id="comp-status" value={formCompany.status} onChange={e => setFormCompany(c => ({ ...c, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="submit">Save</button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
              </div>
            </form>
          ) : (
            <>
              <div className="table-toolbar">
                <input
                  type="text"
                  placeholder="Filter companies..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="table-filter-input"
                />
              </div>
              <div className="items-list">
                <table className="items-table">
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => setSortConfig(s => ({ key: 'company_name', dir: s.key === 'company_name' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Company {sortConfig.key === 'company_name' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                      <th className="sortable hide-on-mobile" onClick={() => setSortConfig(s => ({ key: 'address', dir: s.key === 'address' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Address {sortConfig.key === 'address' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                      <th>Contact</th>
                      <th>Email</th>
                      <th className="sortable" onClick={() => setSortConfig(s => ({ key: 'status', dir: s.key === 'status' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Status {sortConfig.key === 'status' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const list = filterAndSort(companies, filterText, sortConfig.key, sortConfig.dir, ['company_name', 'address', 'contact_person', 'contact_number', 'email']);
                      return list.length === 0 ? (
                        <tr><td colSpan={6} className="empty-row">{companies.length === 0 ? 'No companies yet. Click "+ Add Company" to add one.' : 'No matches for filter.'}</td></tr>
                      ) : list.map(c => (
                        <tr key={c.id}>
                          <td><strong>{c.company_name}</strong></td>
                          <td className="table-desc hide-on-mobile" title={c.address}>{c.address || '—'}</td>
                          <td>{c.contact_person || '—'} {c.contact_number && ` · ${c.contact_number}`}</td>
                          <td>{c.email || '—'}</td>
                          <td><span className={`status-badge status-${c.status}`}>{c.status}</span></td>
                          <td>
                            <div className="item-actions item-actions-icons">
                              <button type="button" className="btn-action btn-edit" onClick={() => { setEditing(c); setFormCompany({ company_name: c.company_name, address: c.address || '', contact_person: c.contact_person || '', contact_number: c.contact_number || '', email: c.email || '', status: c.status }); setShowForm(true); }} aria-label="Edit"><EditIcon /><span className="btn-action-text">Edit</span></button>
                              <button type="button" className="btn-action btn-delete" onClick={async () => { if (window.confirm('Delete company?')) try { await api.delete(`/companies/${normId(c)}`); loadCompanies(); toast.success('Company deleted'); } catch (e) { toast.error(e.response?.data?.error || 'Cannot delete'); } }} aria-label="Delete"><DeleteIcon /><span className="btn-action-text">Delete</span></button>
                            </div>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {subTab === 'employees' && (
        <>
          <div className="list-header">
            <h2>Employees</h2>
            <button className="btn-add" onClick={() => { setShowForm(true); setEditing(null); setFormEmployee({ full_name: '', email: '', phone: '', designation: '', company_id: '', joining_date: '', login_username: '', password: '', status: 'active' }); }}>+ Add Employee</button>
          </div>
          {showForm ? (
            <form className="employees-form" onSubmit={handleSaveEmployee}>
              <h3>{editing ? 'Edit' : 'Add'} Employee</h3>
              <div className="form-field">
                <label htmlFor="emp-full_name">Full name</label>
                <input id="emp-full_name" value={formEmployee.full_name} onChange={e => setFormEmployee(em => ({ ...em, full_name: e.target.value }))} required />
              </div>
              <div className="form-field">
                <label htmlFor="emp-email">Email</label>
                <input id="emp-email" type="email" value={formEmployee.email} onChange={e => setFormEmployee(em => ({ ...em, email: e.target.value }))} />
              </div>
              <div className="form-field">
                <label htmlFor="emp-phone">Phone</label>
                <input id="emp-phone" value={formEmployee.phone} onChange={e => setFormEmployee(em => ({ ...em, phone: e.target.value }))} />
              </div>
              <div className="form-field">
                <label htmlFor="emp-designation">Designation</label>
                <input id="emp-designation" value={formEmployee.designation} onChange={e => setFormEmployee(em => ({ ...em, designation: e.target.value }))} />
              </div>
              <div className="form-field">
                <label htmlFor="emp-company_id">Company</label>
                <select id="emp-company_id" value={formEmployee.company_id} onChange={e => setFormEmployee(em => ({ ...em, company_id: e.target.value }))} required>
                  <option value="">Select Company</option>
                  {companies.filter(c => c.status === 'active').map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="emp-joining_date">Joining date</label>
                <input id="emp-joining_date" type="date" value={formEmployee.joining_date} onChange={e => setFormEmployee(em => ({ ...em, joining_date: e.target.value }))} />
              </div>
              <div className="form-field">
                <label htmlFor="emp-login_username">Login username</label>
                <input id="emp-login_username" value={formEmployee.login_username} onChange={e => setFormEmployee(em => ({ ...em, login_username: e.target.value }))} required disabled={!!editing} />
              </div>
              <div className="form-field">
                <label htmlFor="emp-password">{editing ? 'New password (leave blank to keep)' : 'Password'}</label>
                <input id="emp-password" type="password" value={formEmployee.password} onChange={e => setFormEmployee(em => ({ ...em, password: e.target.value }))} required={!editing} placeholder={editing ? 'Leave blank to keep current' : ''} />
              </div>
              <div className="form-field">
                <label htmlFor="emp-status">Status</label>
                <select id="emp-status" value={formEmployee.status} onChange={e => setFormEmployee(em => ({ ...em, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="submit">Save</button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
              </div>
            </form>
          ) : (
            <>
              <div className="table-toolbar">
                <input
                  type="text"
                  placeholder="Filter employees..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="table-filter-input"
                />
              </div>
              <div className="items-list">
                <table className="items-table">
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => setSortConfig(s => ({ key: 'full_name', dir: s.key === 'full_name' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Name {sortConfig.key === 'full_name' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                      <th className="sortable" onClick={() => setSortConfig(s => ({ key: 'company_name', dir: s.key === 'company_name' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Company {sortConfig.key === 'company_name' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                      <th className="hide-on-mobile">Designation</th>
                      <th>Email / Phone</th>
                      <th className="sortable" onClick={() => setSortConfig(s => ({ key: 'status', dir: s.key === 'status' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Status {sortConfig.key === 'status' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const list = filterAndSort(employees, filterText, sortConfig.key, sortConfig.dir, ['full_name', 'company_name', 'designation', 'email', 'phone', 'employee_code']);
                      return list.length === 0 ? (
                        <tr><td colSpan={6} className="empty-row">{employees.length === 0 ? 'No employees yet. Click "+ Add Employee" to add one.' : 'No matches for filter.'}</td></tr>
                      ) : list.map(em => (
                        <tr key={em.id}>
                          <td><strong>{em.full_name}</strong></td>
                          <td>{em.company_name || '—'}</td>
                          <td className="hide-on-mobile">{em.designation || '—'}</td>
                          <td>{em.email || '—'} {em.phone && ` · ${em.phone}`}</td>
                          <td><span className={`status-badge status-${em.status}`}>{em.status}</span></td>
                          <td>
                            <div className="item-actions item-actions-icons">
                              <button type="button" className="btn-action btn-edit" onClick={() => { setEditing(em); setFormEmployee({ full_name: em.full_name, email: em.email || '', phone: em.phone || '', designation: em.designation || '', company_id: em.company_id || '', joining_date: (em.joining_date || '').slice(0, 10), login_username: em.login_username, password: '', status: em.status }); setShowForm(true); }} aria-label="Edit"><EditIcon /><span className="btn-action-text">Edit</span></button>
                              {em.status === 'active' && <button type="button" className="btn-action btn-delete" onClick={() => handleDeactivateEmployee(em)} aria-label="Deactivate"><DeleteIcon /><span className="btn-action-text">Deactivate</span></button>}
                            </div>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {subTab === 'attendance' && (
        <>
          <div className="list-header">
            <h2>Attendance</h2>
            <button className="btn-add" onClick={() => { setShowForm(true); setEditing(null); setFormAttendance({ employee_id: '', date: new Date().toISOString().slice(0, 10), punch_in_time: '', punch_out_time: '' }); }}>+ Add Record</button>
            <button className="btn-download" onClick={handleDownloadReport}>Download Report (Excel)</button>
          </div>
          <div className="attendance-filters">
            <div className="filter-field">
              <label htmlFor="att-filter-company">Company</label>
              <select id="att-filter-company" value={filters.company_id} onChange={e => setFilters(f => ({ ...f, company_id: e.target.value }))}>
                <option value="">All Companies</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </div>
            <div className="filter-field">
              <label htmlFor="att-filter-employee">Employee name</label>
              <select id="att-filter-employee" value={filters.employee_id} onChange={e => setFilters(f => ({ ...f, employee_id: e.target.value }))}>
                <option value="">All Employees</option>
                {employees.map(em => <option key={em.id} value={em.id}>{em.company_name ? `${em.full_name} (${em.company_name})` : em.full_name}</option>)}
              </select>
            </div>
            <div className="filter-field">
              <label htmlFor="att-filter-month">Month</label>
              <select id="att-filter-month" value={filters.month} onChange={e => setFilters(f => ({ ...f, month: e.target.value }))}>
                <option value="">All Months</option>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}</option>)}
              </select>
            </div>
            <div className="filter-field">
              <label htmlFor="att-filter-year">Year</label>
              <input id="att-filter-year" type="number" value={filters.year} onChange={e => setFilters(f => ({ ...f, year: e.target.value }))} min="2020" max="2030" />
            </div>
          </div>
          {showForm ? (
            <form className="employees-form" onSubmit={handleSaveAttendance}>
              <h3>{editing ? 'Edit' : 'Add'} Attendance</h3>
              {!editing && (
                <>
                  <div className="form-field">
                    <label htmlFor="att-employee_id">Employee name</label>
                    <select id="att-employee_id" value={formAttendance.employee_id} onChange={e => setFormAttendance(a => ({ ...a, employee_id: e.target.value }))} required>
                      <option value="">Select Employee</option>
                      {employees.map(em => <option key={em.id} value={em.id}>{em.company_name ? `${em.full_name} (${em.company_name})` : em.full_name}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label htmlFor="att-date">Date</label>
                    <input id="att-date" type="date" value={formAttendance.date} onChange={e => setFormAttendance(a => ({ ...a, date: e.target.value }))} required />
                  </div>
                </>
              )}
              <div className="form-field">
                <label htmlFor="att-punch_in">Punch in time</label>
                <input id="att-punch_in" type="time" value={formAttendance.punch_in_time} onChange={e => setFormAttendance(a => ({ ...a, punch_in_time: e.target.value }))} />
              </div>
              <div className="form-field">
                <label htmlFor="att-punch_out">Punch out time</label>
                <input id="att-punch_out" type="time" value={formAttendance.punch_out_time} onChange={e => setFormAttendance(a => ({ ...a, punch_out_time: e.target.value }))} />
              </div>
              <div className="form-actions">
                <button type="submit">Save</button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
              </div>
            </form>
          ) : null}
          {loading ? <p>Loading attendance...</p> : (
            <div className="attendance-table-wrap">
              <div className="table-toolbar" style={{ marginBottom: '1rem' }}>
                <input
                  type="text"
                  placeholder="Filter by employee or company..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="table-filter-input"
                />
              </div>
              <table className="attendance-table items-table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => setSortConfig(s => ({ key: 'employee_name', dir: s.key === 'employee_name' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Employee {sortConfig.key === 'employee_name' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                    <th className="sortable" onClick={() => setSortConfig(s => ({ key: 'company_name', dir: s.key === 'company_name' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Company {sortConfig.key === 'company_name' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                    <th className="sortable" onClick={() => setSortConfig(s => ({ key: 'date', dir: s.key === 'date' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Date {sortConfig.key === 'date' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                    <th>Punch In</th>
                    <th>Punch Out</th>
                    <th>Hours</th>
                    <th>Added by</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const list = filterAndSort(attendances, filterText, sortConfig.key, sortConfig.dir, ['employee_name', 'company_name', 'date']);
                    return list.length === 0 ? (
                      <tr><td colSpan={8} className="empty-row">{attendances.length === 0 ? 'No attendance records for selected filters.' : 'No matches for filter.'}</td></tr>
                    ) : list.map(a => (
                    <tr key={a.id}>
                      <td>{a.employee_name}</td>
                      <td>{a.company_name}</td>
                      <td>{a.date}</td>
                      <td>{a.punch_in_time || '—'}</td>
                      <td>{a.punch_out_time || '—'}</td>
                      <td>{a.working_hours != null ? a.working_hours : '—'}</td>
                      <td>{a.admin_added ? 'Admin' : a.created_by}</td>
                      <td><button className="btn-small" onClick={() => openEditAttendance(a)}>Edit</button></td>
                    </tr>
                  ));
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminEmployeesSection;
